import React from 'react';
import { Folder, MoreVertical } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown } from 'Common/Components/Dropdown';

interface KnowledgeCardProps {
  knowledge: any;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onSettings?: (id: string) => void;
}

const KnowledgeCard: React.FC<KnowledgeCardProps> = ({
  knowledge,
  onEdit,
  onDelete,
  onSettings
}) => {
  const navigate = useNavigate();

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.no-nav')) return; // Ignore clicks inside dropdown/actions area
    navigate(`/knowledge/${knowledge?.id}/documents`);
  };

  const handleDelete = (id: string) => {
    onDelete?.(id);
  };

  const handleSettings = (id: string) => {
    onSettings?.(id);
  };

  return (
    <div
      onClick={handleCardClick}
      className="group/card relative flex flex-col bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow duration-200 p-3 sm:p-4 cursor-pointer"
    >
      {/* Header with folder icon and title */}
      <div className="flex items-start space-x-2 sm:space-x-3 mb-2 sm:mb-3">
        <div className="flex-shrink-0">
          <Folder className="w-6 h-6 sm:w-8 sm:h-8 rounded-sm bg-blue-50 text-blue-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm sm:text-base font-medium text-gray-900 truncate">
            {knowledge?.name}
          </h3>
          {/* Stats row */}
          <div className="flex items-center space-x-2 sm:space-x-4 text-[10px] sm:text-xs text-gray-500 mb-2 sm:mb-3">
            <span>{knowledge?.documents?.length || 0} docs <span className='ms-1'>•</span></span>
            {/* You can uncomment these if you have the data */}
            {/* <span>{(knowledge?.word_count / 1000).toFixed(0) || 0} k words <span className='ms-1'>•</span></span> */}
            {/* <span>{knowledge?.app_count || 0} linked apps</span> */}
          </div>
        </div>
      </div>

      {/* Description */}
      <div
        className="flex-1 text-[10px] sm:text-[11px] text-gray-600 overflow-hidden mb-3 sm:mb-4 line-clamp-3"
      >
        {knowledge.description}
      </div>

      <div className='mt-auto ms-auto group-hover/card:opacity-100 opacity-0 relative no-nav'>
        <div className="no-nav">
          <Dropdown className="relative transition-all duration-200 ease-linear dropdown shrink-0 group-[.right]/item:order-1 no-nav">
            <Dropdown.Trigger
              type="button" className="dropdown-toggle no-nav p-1 sm:p-2 hover:bg-gray-100 rounded" id="dropdownMenuButton" data-bs-toggle="dropdown">
              <MoreVertical className="inline-block size-3 sm:size-4" />
            </Dropdown.Trigger>

            <Dropdown.Content placement="workflow" className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right list-none bg-white rounded-md shadow-md dropdown-menu min-w-[8rem] sm:min-w-[10rem] dark:bg-zink-600 no-nav" aria-labelledby="dropdownMenuButton">
              <li>
                <Link className="block px-3 py-1.5 sm:px-4 sm:py-1.5 text-xs sm:text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200 no-nav" to="#!" onClick={() => handleSettings(knowledge.id)} >Settings</Link>
              </li>
              <li>
                <Link className="block px-3 py-1.5 sm:px-4 sm:py-1.5 text-xs sm:text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200 no-nav" to="#!" onClick={() => handleDelete(knowledge.id)} >Delete</Link>
              </li>
            </Dropdown.Content>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeCard;